import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle2, RefreshCw, FileSearch } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const authCfg = () => {
  const t = localStorage.getItem('mosaic_session_token');
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

// v6.32.92 — Super Admin tile that calls GET /api/superadmin/anomaly-report and
// renders the cosmetic / hygiene findings so the operator can catch drift
// BEFORE it ages into a cleanup incident. Read-only, no mutations.
const CATEGORY_META = {
  orders_with_invalid_email: {
    title: 'Orders with invalid contact email',
    help: 'Bill_to or ship_to contact_email fails RFC 5322 validation. Fix via the "edit" link on the bill_to / ship_to card in the order detail.',
    color: 'bg-rose-50 border-rose-200 text-rose-900',
    badge: 'bg-rose-100 text-rose-700 border-rose-300',
  },
  orders_with_all_caps_school: {
    title: 'Orders with ALL-CAPS school_name',
    help: 'Cosmetic — the school name is stored in ALL CAPS. Fix via the contacts edit modal for cleaner invoices / packing slips.',
    color: 'bg-amber-50 border-amber-200 text-amber-900',
    badge: 'bg-amber-100 text-amber-700 border-amber-300',
  },
  orders_with_all_caps_contact: {
    title: 'Orders with ALL-CAPS contact_name',
    help: 'Cosmetic — the person\'s name is stored in ALL CAPS. Fix via the contacts edit modal.',
    color: 'bg-amber-50 border-amber-200 text-amber-900',
    badge: 'bg-amber-100 text-amber-700 border-amber-300',
  },
  portal_users_expired_not_cleaned: {
    title: 'Portal users expired >90 days',
    help: 'Portal users whose status has been "expired" for more than 90 days. Consider hard-deleting for hygiene (preserves audit trail on portal_user_audit_log).',
    color: 'bg-gray-50 border-gray-200 text-gray-900',
    badge: 'bg-gray-100 text-gray-700 border-gray-300',
  },
  portal_user_email_mismatch: {
    title: 'Portal user email mismatches order bill_to',
    help: 'Active portal user email does NOT match the linked order\'s bill_to.contact_email. Often a typo on one side or the other — investigate before fixing.',
    color: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    badge: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  },
  duplicate_portal_users_per_order: {
    title: 'Duplicate active portal users on same order',
    help: 'More than one active portal user is gating the same order. Could be intentional (co-teacher access) or a typo duplicate — verify before revoking.',
    color: 'bg-purple-50 border-purple-200 text-purple-900',
    badge: 'bg-purple-100 text-purple-700 border-purple-300',
  },
};

export default function AnomalyReportTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/api/superadmin/anomaly-report`, authCfg());
      setReport(r.data);
      if (r.data.total_findings === 0) {
        toast.success('Clean report — no anomalies found');
      } else {
        toast.info(`${r.data.total_findings} finding(s) across ${Object.values(r.data.findings).filter((v) => v.length > 0).length} categories`);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      toast.error('Failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    run();
  }, []);

  return (
    <div className="space-y-4" data-testid="anomaly-report-tab">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileSearch size={20} /> Anomaly Report
          </h2>
          <p className="text-xs text-gray-600 mt-1 max-w-2xl">
            Scans orders + portal users for cosmetic / hygiene drift: invalid emails, ALL-CAPS fields, duplicate portal users, stale expired accounts, email mismatches. Read-only — no data is changed.
          </p>
        </div>
        <Button
          onClick={run}
          disabled={loading}
          variant="outline"
          className="gap-2"
          data-testid="anomaly-refresh"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          {loading ? 'Scanning…' : 'Refresh'}
        </Button>
      </div>

      {error && (
        <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-xs text-rose-800" data-testid="anomaly-error">
          {error}
        </div>
      )}

      {report && (
        <div className="rounded-md bg-gray-50 border border-gray-200 p-3 text-xs text-gray-700 grid grid-cols-4 gap-3" data-testid="anomaly-summary">
          <div><div className="text-[10px] uppercase text-gray-500">Generated</div><div className="font-mono">{report.generated_at.slice(0, 19).replace('T', ' ')} UTC</div></div>
          <div><div className="text-[10px] uppercase text-gray-500">Orders scanned</div><div className="font-semibold">{report.orders_scanned}</div></div>
          <div><div className="text-[10px] uppercase text-gray-500">Portal users scanned</div><div className="font-semibold">{report.portal_users_scanned}</div></div>
          <div><div className="text-[10px] uppercase text-gray-500">Total findings</div><div className={`font-bold ${report.total_findings === 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{report.total_findings}</div></div>
        </div>
      )}

      {report && report.total_findings === 0 && (
        <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="anomaly-clean">
          <CheckCircle2 className="mx-auto text-emerald-600" size={40} />
          <div className="mt-2 text-sm font-semibold text-emerald-900">All clean — no anomalies found</div>
          <div className="text-xs text-emerald-700 mt-1">Production data is free of ALL-CAPS drift, invalid emails, duplicate portal users, and stale accounts.</div>
        </div>
      )}

      {report && report.total_findings > 0 && (
        <div className="space-y-3">
          {Object.entries(report.findings).map(([cat, items]) => {
            if (!items || items.length === 0) return null;
            const meta = CATEGORY_META[cat] || {
              title: cat,
              help: '',
              color: 'bg-gray-50 border-gray-200',
              badge: 'bg-gray-100',
            };
            return (
              <div
                key={cat}
                className={`rounded-lg border ${meta.color} p-3`}
                data-testid={`anomaly-category-${cat}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-sm">{meta.title}</div>
                      <div className="text-xs opacity-80 mt-0.5">{meta.help}</div>
                    </div>
                  </div>
                  <Badge variant="outline" className={meta.badge}>{items.length}</Badge>
                </div>

                <div className="mt-2 space-y-1">
                  {items.map((it, i) => (
                    <div
                      key={i}
                      className="text-xs bg-white/60 rounded px-2 py-1.5 border border-black/5 font-mono"
                      data-testid={`anomaly-item-${cat}-${i}`}
                    >
                      {it.order_number && <span className="font-semibold text-indigo-700">{it.order_number}</span>}
                      {it.portal_user_id && <span className="font-semibold text-indigo-700">{it.portal_user_id}</span>}
                      {it.side && <span className="ml-2 text-[10px] uppercase text-gray-500">{it.side}.{it.field}</span>}
                      {it.field && !it.side && <span className="ml-2 text-[10px] uppercase text-gray-500">{it.field}</span>}
                      <span className="ml-2 text-gray-700">{it.value || it.email || ''}</span>
                      {it.portal_user_email && <span className="ml-2 text-gray-700">{it.portal_user_email}</span>}
                      {it.order_bill_to_email && (
                        <span className="ml-2 text-gray-500">
                          vs bill_to={it.order_bill_to_email}
                        </span>
                      )}
                      {it.age_days != null && (
                        <span className="ml-2 text-gray-500">
                          expired {it.age_days}d ago
                        </span>
                      )}
                      {it.emails && (
                        <span className="ml-2 text-gray-700">
                          {it.emails.join(', ')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
